import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import { AlertTypes, Leap4Breadcrumbs, Mui, MuiIcons, setAlert } from '@leap4/frontend';
import { urlService } from '../../routing/urlsService';
import { failureLabelFor, ISweepStatus } from './appAdsLines.interface';
import { errorMessage, fetchCheckAllLog, fetchCheckAllStatus, startCheckAll } from './appAdsLines.api';
import { LastCheckPanel } from './lastCheckPanel';
import { AppAdsLinesTabs } from './appAdsLinesTabs';
import { BackButton } from './backButton';
import PublishersTab from './publishersTab';
import LinesTab from './linesTab';

const {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Typography,
} = Mui;
const { Refresh: RefreshIcon, Toc: TocIcon } = MuiIcons;

const connector = connect(() => ({}), { setAlert });
type PropsFromRedux = ConnectedProps<typeof connector>;

/**
 * The Publisher Lines shell.
 *
 * Publishers and lines are two ways of looking at the same data, not a parent and a child, so
 * they're sibling tabs rather than a list with a drill-down button. Each tab carries only its
 * own actions; anything spanning both - "Update all", and the log of what it found - lives up
 * here, above the tabs.
 *
 * The tab is driven by the route rather than local state, so /app-ads-lines/all stays a real,
 * shareable link.
 */
const AppAdsLinesManager: React.FC<PropsFromRedux> = ({ setAlert }) => {
    const location = useLocation();
    const queryClient = useQueryClient();

    const [sweeping, setSweeping] = useState(false);
    const [checkAllResult, setCheckAllResult] = useState<ISweepStatus | null>(null);

    const onLinesTab = location.pathname === urlService.appAdsLinesAll();

    const { data: sweepLogs } = useQuery('checkAllLog', fetchCheckAllLog, {
        refetchOnWindowFocus: false,
    });

    /**
     * Sweeps run detached server-side, so this only starts one; progress arrives by polling.
     *
     * A 409 means one is already running for this seat - another tab, or before a reload - so
     * attach to it rather than reporting an error.
     */
    const checkAllMutation = useMutation(() => startCheckAll(), {
        onSuccess: () => setSweeping(true),
        onError: (err: any) => {
            if (err?.response?.status === 409) {
                setSweeping(true);
                setAlert({ msg: 'A check was already running — showing its progress.', type: AlertTypes.SUCCESS });
                return;
            }
            setAlert({ msg: `Could not start the check: ${errorMessage(err)}`, type: AlertTypes.FAILURE });
        },
    });

    const { data: sweep } = useQuery('checkAllStatus', fetchCheckAllStatus, {
        enabled: sweeping,
        refetchInterval: sweeping ? 2000 : false,
        refetchOnWindowFocus: false,
    });

    // A sweep already in flight when the page loads (a reload mid-sweep, or another tab) should
    // be picked up rather than looking as though nothing is happening.
    useEffect(() => {
        let cancelled = false;
        fetchCheckAllStatus()
            .then((status) => {
                if (!cancelled && status?.running) setSweeping(true);
            })
            .catch(() => {
                // Nothing to resume; the button still works.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!sweeping || !sweep || sweep.running) return;
        setSweeping(false);
        setCheckAllResult(sweep);
        queryClient.invalidateQueries('trackedPublishers');
        queryClient.invalidateQueries('distinctLineSpecs');
        // Refresh the persisted log too, so the panel reflects the run that just ended even if
        // the summary dialog is dismissed without being read.
        queryClient.invalidateQueries('checkAllLog');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sweeping, sweep]);

    /** Failed reads grouped by cause, so each group carries one shared instruction. */
    const failuresByKind = React.useMemo(() => {
        const groups = new Map<string, ISweepStatus['results']>();
        (checkAllResult?.results || [])
            .filter((r) => !r.ok)
            .forEach((r) => {
                const kind = r.failureKind || 'other';
                if (!groups.has(kind)) groups.set(kind, []);
                groups.get(kind).push(r);
            });
        return Array.from(groups.entries());
    }, [checkAllResult]);

    /** Successful checks that actually moved something - the rest is noise in a 50-row sweep. */
    const changed = React.useMemo(
        () => (checkAllResult?.results || []).filter((r) => r.ok && (r.stats?.changed ?? 0) > 0),
        [checkAllResult],
    );

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Leap4Breadcrumbs breadcrumbs={[{ text: 'Publisher Lines', icon: <TocIcon /> }]} />
                {/* The feature's root, so its parent is outside it. */}
                <BackButton to={urlService.app()} />
            </Box>

            <AppAdsLinesTabs
                action={
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<RefreshIcon />}
                        disabled={sweeping || checkAllMutation.isLoading}
                        onClick={() => checkAllMutation.mutate()}
                    >
                        {sweeping ? 'Updating…' : 'Update all'}
                    </Button>
                }
            />

            {sweeping && (
                <Box my={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {sweep?.total
                            ? `Checking publishers — ${sweep.completed} of ${sweep.total} done` +
                              (sweep.failed ? `, ${sweep.failed} couldn't be read` : '')
                            : "Starting — reading each publisher's live app-ads.txt."}{' '}
                        You can leave this page; the check carries on.
                    </Typography>
                    <LinearProgress
                        variant={sweep?.total ? 'determinate' : 'indeterminate'}
                        value={sweep?.total ? (sweep.completed / sweep.total) * 100 : undefined}
                    />
                </Box>
            )}

            {/* Collapsed by default. The summary used to be modal-only, so navigating away a
                moment before a sweep finished lost the result with no way back to it. */}
            {!sweeping && (
                <Box mt={2}>
                    <LastCheckPanel logs={sweepLogs || []} />
                </Box>
            )}

            <Box mt={2}>{onLinesTab ? <LinesTab /> : <PublishersTab />}</Box>

            <Dialog open={!!checkAllResult} onClose={() => setCheckAllResult(null)} fullWidth maxWidth="sm">
                <DialogTitle>Update finished</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" gutterBottom>
                        {checkAllResult?.checked} publisher(s) checked, {checkAllResult?.failed} could not be read.
                    </Typography>

                    {/* Failures first and grouped by cause: they're the only rows needing action,
                        and they were previously lost among dozens of "no changes". */}
                    {!!failuresByKind.length && (
                        <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>
                                Needs attention
                            </Typography>
                            {failuresByKind.map(([kind, rows]) => (
                                <Box key={kind} mb={1.5}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {failureLabelFor(rows[0].failureLabel, kind)} ({rows.length})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {rows[0].failureAction}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        {rows.map((r) => r.name).join(', ')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {!!changed.length && (
                        <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>
                                Status changes
                            </Typography>
                            {changed.map((r) => (
                                <Typography variant="body2" key={r.publisherId}>
                                    <strong>{r.name}</strong> — {r.summary}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {checkAllResult?.error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            The check stopped early: {checkAllResult.error}
                        </Alert>
                    )}

                    {!failuresByKind.length && !changed.length && !checkAllResult?.error && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Every publisher read successfully, with no status changes.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCheckAllResult(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default connector(AppAdsLinesManager);
